// Вся логика календаря и выбора времени.

'use client';

import {
  addDays,
  format,
  isSameDay,
  nextSaturday,
  setHours,
  setMinutes,
  startOfDay,
} from 'date-fns';
import { ru } from 'date-fns/locale';
import { Calendar as CalendarIcon, Inbox, Sofa, Sun } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { Button } from '../../../components/ui/button';
import { Calendar } from '../../../components/ui/calendar';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';

interface DatePickerProps {
  date?: Date;
  onDateChange: (date?: Date) => void;
}

const DateDisplayIcon = ({ date, icon: Icon }: { date: Date; icon: React.ElementType }) => {
  const day = format(date, 'd');
  return (
    <div className="relative w-5 h-5 mr-2 flex items-center justify-center">
      <Icon size={20} className="text-slate-500" />
      <span className="absolute text-[10px] font-bold text-slate-600 mt-0.5">{day}</span>
    </div>
  );
};

// ИСПРАВЛЕНО: Добавлен интерфейс вместо any
interface DateButtonProps {
  label: string;
  date?: Date | null; // Может быть null (для "Без срока")
  icon?: React.ElementType;
  onClick: () => void;
  isActive?: boolean;
  dateIcon?: React.ReactNode;
}

const DateButton = ({ label, date, icon: Icon, onClick, isActive, dateIcon }: DateButtonProps) => (
  <Button
    variant="ghost"
    className={cn(
      'w-full justify-start font-normal text-slate-800 h-9',
      isActive && 'bg-slate-200 text-slate-900',
    )}
    onClick={onClick}
  >
    {/* ИСПРАВЛЕНО: Добавлена проверка Icon &&, чтобы TS не ругался на рендер undefined */}
    {dateIcon ? dateIcon : Icon && <Icon size={16} className="mr-2 text-slate-500" />}
    {label}
    {date && (
      <span className="ml-auto text-slate-500 text-xs">{format(date, 'EEE', { locale: ru })}</span>
    )}
  </Button>
);

export function DatePicker({ date, onDateChange }: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [timeValue, setTimeValue] = useState('');
  const timeSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (date) {
      if (date.getHours() !== 0 || date.getMinutes() !== 0) {
        setTimeValue(format(date, 'HH:mm'));
      } else {
        setTimeValue('');
      }
    } else {
      setTimeValue('');
    }
  }, [date]);

  const handleDateSelect = (d?: Date, shouldScroll: boolean = false) => {
    const newDate = d ? new Date(d) : undefined;
    if (newDate && date && (date.getHours() !== 0 || date.getMinutes() !== 0)) {
      newDate.setHours(date.getHours(), date.getMinutes());
    }
    onDateChange(newDate);

    if (shouldScroll) {
      setTimeout(() => {
        timeSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 50);
    }
  };

  const handleTimeChange = (newTime: string) => {
    if (!date) {
      toast.error('Сначала выберите дату');
      return;
    }
    setTimeValue(newTime);
    if (!newTime) return;

    const [hours, minutes] = newTime.split(':').map(Number);
    const newDate = setMinutes(setHours(new Date(date), hours), minutes);
    onDateChange(newDate);
  };

  const today = startOfDay(new Date());
  const tomorrow = addDays(today, 1);
  const weekend = nextSaturday(today);

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <Popover open={open} onOpenChange={setOpen} modal={false}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600"
                >
                  <CalendarIcon size={16} className="mr-2" />
                  {date ? format(date, 'dd MMM', { locale: ru }) : 'Срок'}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="z-[1000] w-auto p-0 bg-slate-100 border-slate-300 max-h-[min(85vh,var(--radix-popper-available-height))] overflow-y-auto"
                align="start"
                side="bottom"
                sideOffset={8}
                collisionPadding={12}
                avoidCollisions
                sticky="always"
                updatePositionStrategy="always"
                onWheel={(e) => e.stopPropagation()}
              >
                <div className="p-1 flex flex-col text-sm">
                  <DateButton
                    label="Сегодня"
                    date={today}
                    dateIcon={<DateDisplayIcon date={today} icon={Sun} />}
                    isActive={!!date && isSameDay(date, today)}
                    onClick={() => handleDateSelect(today, true)}
                  />
                  <DateButton
                    label="Завтра"
                    date={tomorrow}
                    dateIcon={<DateDisplayIcon date={tomorrow} icon={Sun} />}
                    isActive={!!date && isSameDay(date, tomorrow)}
                    onClick={() => handleDateSelect(tomorrow, true)}
                  />
                  <DateButton
                    label="На выходных"
                    date={weekend}
                    dateIcon={<DateDisplayIcon date={weekend} icon={Sofa} />}
                    isActive={!!date && isSameDay(date, weekend)}
                    onClick={() => handleDateSelect(weekend, true)}
                  />
                  <hr className="border-slate-300 my-1" />
                  <DateButton
                    label="Без срока"
                    date={null}
                    icon={Inbox}
                    isActive={!date}
                    onClick={() => {
                      handleDateSelect(undefined);
                      setOpen(false);
                    }}
                  />
                </div>
                <hr className="border-slate-300" />
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => handleDateSelect(d || undefined)}
                  initialFocus
                  locale={ru}
                  className="text-slate-900"
                  disabled={{ before: today }}
                  // ✅ ГЛАВНОЕ ИСПРАВЛЕНИЕ: Используем modifiersClassNames
                  modifiers={{ today: today }}
                  modifiersClassNames={{
                    // Делаем прошедшие дни видимыми, но тусклыми и неактивными
                    disabled: 'text-slate-400 opacity-50 cursor-not-allowed',
                    // Подсвечиваем сегодняшний день (даже если он не выбран)
                    today: 'bg-slate-200 rounded-md font-bold',
                  }}
                />
                <div className="p-2 border-t border-slate-300" ref={timeSectionRef}>
                  <Label className="text-xs text-slate-600">Время</Label>
                  <Input
                    type="time"
                    disabled={!date}
                    value={timeValue}
                    onChange={(e) => handleTimeChange(e.target.value)}
                    className="w-full bg-white text-slate-800 p-2 rounded-md mt-1 border border-slate-300"
                  />
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-slate-700"
                      disabled={!date}
                      onClick={() => handleTimeChange('09:00')}
                    >
                      Утро (09:00)
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-slate-700"
                      disabled={!date}
                      onClick={() => handleTimeChange('14:00')}
                    >
                      День (14:00)
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 gap-2 mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-slate-700"
                      disabled={!date}
                      onClick={() => handleTimeChange('19:00')}
                    >
                      Вечер (19:00)
                    </Button>
                  </div>
                  <div className="flex justify-center mt-3">
                    <Button
                      size="sm"
                      onClick={() => setOpen(false)}
                      disabled={!date}
                      className="bg-sky-600 hover:bg-sky-700 w-full"
                    >
                      Ок
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>Выбрать дату</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
